import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import { useFormContext } from "react-hook-form";
import { useEffect } from "react";
import { Button } from "../components/ui/button.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CreateDemandRequest } from "@ns/api";
import { toaster } from "../components/ui/toaster.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/custom-api.ts";
import {
  CreateDemandSchema,
  createDemandSchema,
} from "../schemas/create-demand.schema.ts";
import { LocationSelectProps } from "../utils/types/location.props.ts";
import { ControlledDropdown } from "../components/form/controlled-dropdown.tsx";

const CreateDemandForm = () => {
  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = useFormContext<CreateDemandSchema>();

  const navigate = useNavigate();

  const resetFields = () => {
    reset({
      to: [""],
      quantity: "",
    });
  };

  useEffect(() => {
    resetFields();
  }, []);

  const { id } = useParams();

  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (request: CreateDemandRequest) =>
      api.demand.createDemand(request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi nalog!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Novi nalog je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      resetFields();
      queryClient.invalidateQueries({ queryKey: ["demands", id] });
      navigate(`/warehouses/${id}/demands`);
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const onSubmit = handleSubmit((data) => {
    console.log("here", data);
    mutate({
      codebookId: id!,
      to: data.to[0],
      quantity: Number(data.quantity),
    });
  });

  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className="text-[24px] text-center text-black font-semibold">
            KREIRAJ TREBOVANJE
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isValid}
              onClick={onSubmit}
            >
              <IoCheckmark /> Potvrdi
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              onClick={() => resetFields()}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <ControlledDropdown
            name={"to"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            options={LocationSelectProps}
            placeholder={"Izaberi ciljanu lokaciju"}
            label={"Lokacija"}
          />
          <ControlledInput
            name={"quantity"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Kolicina"}
            placeholder={"Unesi kolicinu"}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateDemandPage = () => {
  return (
    <ControlledForm schema={createDemandSchema}>
      <CreateDemandForm />
    </ControlledForm>
  );
};
